import React from 'react';
import styled from 'styled-components';
import { SectionName, SectionTitle } from '../common/styles';

const Container = styled.div`
  margin: ${props => (props.theme.isMobile ? '67px 0 19px 19px' : '0 auto')};
  padding-bottom: 6px;
  max-width: 1230px;
  width: 85vw;
  display: flex;
  flex-direction: column;
  align-items: start;
`;

export const Timestamp = styled(SectionName)`
  font-size: 12px;
`;

export const Title = styled(SectionTitle)`
  margin: 6px 0 0 0;
  line-height: 40px;
  height: 46px;
  max-width: 795px;
  border-bottom: 1px dashed ${props => props.theme.primaryColor};
  font-size: ${props => (props.theme.isMobile ? 25 : 40)}px;
`;

export const Div = styled.div`
  position: relative;
  max-width: 1180px;
  width: 82vw;
  color: ${props => props.theme.bodyColor};
  font-size: 16px;
  line-height: 25px;
  text-align: left;
`;

export const Headline = styled(Div)`
  font-size: 20px;
  font-weight: ${props => props.theme.fontWeightBold};
  margin-top: 33px;
`;

export const Disclaimer = styled(Title)`
  border-bottom: none;
`;

export default function Content() {
  return (
    <Container>
      {/* eslint-disable max-len */}
      {/* eslint react/jsx-one-expression-per-line: 0 */}
      <Timestamp>Updated 9 Feb 2022</Timestamp>
      <Title>Privacy Policy</Title>
      <Headline>Introduction</Headline>
      <Div>
        <p>
          This is the Privacy Policy of Livewire Markets Pty Ltd ACN 156 343 501 <b>(Livewire or us/we)</b>.
          &nbsp;We understand the importance of your privacy and your concerns about the security of your personal information.
          &nbsp;We are committed to protecting any personal information about you that we hold.
        </p>
        <p>
          This Privacy Policy outlines how we collect, use, protect, and disclose your Personal Information.
          &nbsp;It also explains how you can access and have it amended, as well as who you can contact should you have an enquiry or complaint.
        </p>
      </Div>
      <Headline>Personal Information</Headline>
      <Div>
        <p>
          &apos;Personal Information&apos; means information or an opinion (whether or not true, and whether or not recorded in a material form)
          &nbsp;about an identified individual, or about an individual who is reasonably identifiable.
        </p>
      </Div>
      <Headline>What kinds of Personal Information do we collect and hold?</Headline>
      <Div>
        <p>
          The types of Personal Information we collect about you will depend on the product or service you receive from us.
          &nbsp;The Personal Information we may collect about you includes, but is not limited to:
        </p>
        <ul>
          <li>Your name, phone number, street address and/or email address</li>
          <li>
            Any information on your social media profile (e.g. LinkedIn) which you agree to link to your account with us,
            &nbsp;such as your employer, employment history, and professional memberships
          </li>
          <li>Your date of birth, gender, employment details, financial details, domicile and citizenship status</li>
          <li>Your credit card number or bank account details</li>
          <li>If you are acting on behalf of your employer, your job title, employer&apos;s name and contact information</li>
          <li>Demographic information, such as age bracket, investor type and how you heard about us</li>
          <li>Account credentials such as your user ID and password for our online products</li>
          <li>Information about your investment profile and interests (including banking and insurance products and security holdings)</li>
          <li>your interests in our products, services or events</li>
          <li>Personal website usage information, including cookies and other information about your computer, device and browser.</li>
        </ul>
        <p>
          Where our clients are corporations, we may collect Personal Information from that corporation about different
          &nbsp;contact persons within the corporation including name, job title, and contact information.
        </p>
        <p>
          We do not adopt Commonwealth Government identifiers, such as a tax file number or a Medicare number, as a means of identifying individuals.
        </p>
      </Div>
      <Headline>How do we collect Personal Information?</Headline>
      <Div>
        <p>
          We will collect Personal Information directly from you or indirectly from third party sources, or as otherwise permitted by law.
          &nbsp;The Personal Information we collect may be captured from a variety of sources including:
        </p>
        <ul>
          <li>
            direct contact with us or use of our website, product or service - for example, by subscribing to our site, completing an online form,
            &nbsp;entering details about our products or services, accepting an invitation from us or when you send us an email.
          </li>
          <li>voluntary submissions (e.g. responses to surveys, requests for information on our products or those of our advertisers/marketing partners)</li>
          <li>registrations/applications to receive our products or services or the products we distribute</li>
          <li>from your employer</li>
          <li>public registers (e.g. if we need to check Personal Information independently of you)</li>
        </ul>
        <p>
          While some contacts with us such as general enquiries may be made anonymously or by using a pseudonym, if the relationship is to progress any further
          &nbsp;(by you becoming our client or having a business relationship with us) it will be necessary for us to know who you are to enable us to deliver
          &nbsp;our services to you. Similarly, we cannot deal with complaints made anonymously or by pseudonym. If you give us information about another person,
          &nbsp;you should make sure that you are authorised to give us that information.  You must also make sure to inform that person of who we are and that
          &nbsp;we may use and disclose their personal information in accordance with this Privacy Policy.
        </p>
        <p>
          Should a third party give us unsolicited Personal Information about you, we will within a reasonable period determine whether or not we could have
          &nbsp;collected the information directly from you or if it is in a public record and, if not, we will take reasonable steps to destroy or de-identify
          &nbsp;that information unless the law otherwise permits or requires.
        </p>
      </Div>
      <Headline>How do we use your Personal Information?</Headline>
      <Div>
        <p>
          We may collect, store, use, and process Personal Information where necessary to provide our products or services to you, to improve our products or services, to manage our relationships with you and to provide you with information about other products and services that we, and our commercial partners offer from time to time.
        </p>
        <p>
          If you would prefer not to receive product and service information, please unsubscribe or contact us to withdraw your consent.
        </p>
        <p>
          We will not make the Personal Information collected using our products and services available to anyone outside Livewire except with your consent or where required by or permitted by law.
        </p>
      </Div>
      <Headline>Cookies</Headline>
      <Div>
        <p>
          A cookie is a small file which can be downloaded on a device when you access our website and is then sent back to our website each time you access our
          &nbsp;website. This website may use cookies. If you do not wish to receive any cookies you may set your browser to refuse cookies. We may use cookies
          &nbsp;to learn about how people use our website, what they are interested in and to help us make a better website.
        </p>
        <p>
          Like most other websites, we may automatically record details about any computer used to access the website such as the date and time of access,
          &nbsp;the IP address, domain name and details of the information accessed. The use of cookies on our website does not identify you or provide
          &nbsp;access to your device.
        </p>
      </Div>
      <Headline>Changes to our privacy policy</Headline>
      <Div>
        <p>
          Sometimes it will be necessary for us to update and change our privacy policy. We reserve the right to change our privacy policy at any time.
          &nbsp;We may notify you about changes to our privacy policy by posting an updated version on our website.
        </p>
      </Div>
      <Headline>Links to other websites</Headline>
      <Div>
        <p>
          Our website may contain links to other websites for you to access. You should be aware that the privacy policies of the operators of those
          &nbsp;other sites may not be the same as ours and you should refer to their privacy policies.
        </p>
      </Div>
      <Headline>Information security</Headline>
      <Div>
        <p>
          We will take all reasonable steps to ensure that any Personal Information about you which we hold is:
        </p>
        <ul>
          <li>Secure: protected from misuse, interference and loss and from unauthorised access, modification or disclosure; and</li>
          <li>Appropriate: accurate, complete, up-to-date, relevant and not misleading having regard to the purpose for which it is held and may be used.</li>
        </ul>
        <p>
          Your Personal Information may be kept in various formats by us, our related companies and relevant third-party providers.
        </p>
      </Div>
      <Headline>Changes to our privacy policy</Headline>
      <Div>
        <p>
          Sometimes it will be necessary for us to update and change our privacy policy. We reserve the right to change our privacy policy at any time.
          &nbsp;We may notify you about changes to our privacy policy by posting an updated version on our website.
        </p>
      </Div>
      <Headline>Access to your information</Headline>
      <Div>
        <p>
          You may request access at any time to Personal Information we hold about you by emailing us at
          &nbsp;<a href="mailto:team@livewiremarkets.com">team@livewiremarkets.com</a> or by contacting us at the address below. We will deal with all
          &nbsp;requests for access to personal information as quickly as possible but may charge you a fee where access is provided.
        </p>
        <p>
          If you have a complaint about how we handle your Personal Information, please email us at
          &nbsp;<a href="mailto:team@livewiremarkets.com">team@livewiremarkets.com</a>
        </p>
      </Div>
      <Disclaimer>Disclaimer</Disclaimer>
      <Div>
        <p>
          Livewire gives readers access to information and educational content provided by financial services professionals and companies
          &nbsp;(&quot;Livewire Contributors&quot;). Livewire does not operate under an Australian financial services licence and relies on the exemption
          &nbsp;available under section 911A(2)(eb) of the Corporations Act 2001 (Cth) in respect of any advice given. Any advice on this site is general
          &nbsp;in nature and does not take into consideration your objectives, financial situation or needs. Before making a decision please consider these
          &nbsp;and any relevant Product Disclosure Statement. Livewire has commercial relationships with some Livewire Contributors.
        </p>
      </Div>
    </Container>
  );
}
